<script setup lang="ts">
import { ref, computed, defineAsyncComponent, watch } from 'vue';

// Importación de componentes de la oferta académica
import TopBannerLeft from '../components/oferta/TopBannerLeft.vue'; 
const DiplomadoLeft = defineAsyncComponent(() => import('../components/oferta/DiplomadoLeft.vue')); 
const DiplomadoRight = defineAsyncComponent(() => import('../components/oferta/DiplomadoRight.vue')); 

// Interfaces
interface DiplomadoData {
  id: string;
  img: string;
  nivel: string;
  carrera: string;
  complemento?: string;
  intro: string;
  beneuno: string;
  benedos: string;
  benetres: string;
  perfil: string;
  alt: string;
  fechas: string;
  duracion: string;
  inicios: boolean[];
}

interface Categoria {
  id: string;
  nombre: string;
  imagenBanner: string;
  altBanner: string;
  diplomados: DiplomadoData[];
}

interface Periodo {
  id: string;
  mes: string;
  periodo: string;
}

// Períodos o Fechas de la tabla
const periodos = ref<Periodo[]>([
  { id: '1', mes: 'Sept.', periodo: '2026' },
  { id: '2', mes: 'Ene.', periodo: '2027' },
  { id: '3', mes: 'Abr.', periodo: '2027' },
  { id: '4', mes: 'Jul.', periodo: '2027' },
  { id: '5', mes: 'Sept.', periodo: '2027' },
  { id: '6', mes: 'Ene.', periodo: '2028' },
]);

// Estructura de categorías y diplomados
const categorias = ref<Categoria[]>([
    {
        id: 'psicologia',
        nombre: 'Psicología y Educación',
        imagenBanner: '/pagesImages/diplomados-psicologia.webp',
        altBanner: 'Estudiantes de psicología',
        diplomados: [
            {
                id: 'desarrollo-infantil',
                img: '/pagesImages/diplomado-desarrollo-infantil.webp',
                nivel: 'Diplomado en',
                carrera: 'Psicología del desarrollo infantil',
                complemento: ' Enfoques clásicos y contemporáneos',
                intro: 'Constituye una visión integral del desarrollo infantil a partir de las principales teorías psicológicas, con aplicación directa en contextos educativos, clínicos y sociales. Este programa combina solidez teórica con herramientas prácticas para fortalecer el conocimiento sobre la niñez.',
                beneuno: '● Combina perspectivas psicoanalíticas, cognitivas y socioculturales para una comprensión holística de la infancia.',
                benedos: '● Estudio y análisis superior de autores clásicos y contemporáneos del desarrollo infantil.',
                benetres: '● Desarrolla una mirada reflexiva y crítica para diseñar intervenciones efectivas en entornos educativos, clínicos y sociales, promoviendo el bienestar integral de la niñez.',
                perfil: 'Diseñado para profesionales y estudiantes de Psicología, Pedagogía, Educación, Trabajo Social y áreas afines, así como para padres, cuidadores y psicoterapeutas interesados en comprender el mundo interno del niño. Enfocado a perfiles comprometidos con el desarrollo humano en sus múltiples dimensiones.',
                alt: 'Diplomado en Psicología del Desarrollo Infantil Educación Continúa en Universidad Humanitas',
                fechas: 'Septiembre y abril.',
                    duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true,  false, true, false, true, false,]
            },
            {
                id: 'neuroeducacion',
                img: '/pagesImages/diplomado-desarrollo-infantil-neuroeducacion.webp',
                nivel: 'Diplomado en',
                carrera: 'Desarrollo infantil y Neuroeducación',
                intro: 'Programa de educación continua que integra los aportes de la Neurociencia, la Psicología del desarrollo y las ciencias de la educación para comprender de manera integral cómo se desarrollan el cerebro, el aprendizaje y las habilidades socioemocionales durante la infancia.',
                beneuno: '● Fortalece la comprensión integral del desarrollo infantil, al integrar conocimientos sobre desarrollo cerebral, procesos cognitivos, regulación emocional y aprendizaje.',
                benedos: '● Proporciona estrategias de neuroeducación aplicables al aprendizaje.',
                benetres: '● Desarrolla metodologías y uso de herramientas para el acompañamiento infantil y familiar.',
                perfil: 'Dirigido a profesionales, docentes y personas interesadas en comprender los procesos del desarrollo infantil desde una perspectiva Neurocientífica, educativa y socioemocional.',
                alt: 'Niños conviviendo como parte de su desarrollo',
                fechas: 'Enero y julio.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [false, true, false, true, false, true]
            },
            {
                id: 'terapia cognitivo conductual',
                img: '/pagesImages/diplomado-terapia-cognitiva.webp',
                nivel: 'Diplomado en',
                carrera: 'Terapia cognitivo conductual',
                intro: 'Este programa educativo cuenta con un enfoque terapéutico orientado a la modificación de patrones cognitivos y conductuales disfuncionales, con el objetivo primordial de optimizar los estados emocionales y promover un bienestar integral en los individuos.',
                beneuno: '● Dominio de técnicas de vanguardia conformado por un conocimiento profundo y práctico de las estrategias cognitivas y conductuales más efectivas y basadas en evidencia para abordar una amplia gama de desafíos psicológicos.',
                benedos: '● Ampliación del campo de acción profesional al desarrollar las competencias para tratar trastornos prevalentes como la ansiedad, la depresión y el estrés postraumático, afinando los argumentos de un profesional más versátil y demandado.',
                benetres: '● Desarrollo de habilidades clínicas esenciales, desarrollando la capacidad para realizar evaluaciones precisas, diseñar planes de intervención personalizados y establecer una alianza terapéutica sólida y efectiva.',
                perfil: 'Dirigido a a psicólogos, psiquiatras, trabajadores sociales y otros profesionales de la salud mental interesados.',
                alt: 'Diplomado en Terapia cognitivo conductual de Educación Continúa en Universidad Humanitas',
                fechas: 'Septiembre y abril.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true, false, true, false, true, false]
            },
        ]
    },
    {
        id: 'derecho',
        nombre: 'Derecho y criminología',
        imagenBanner: '/pagesImages/diplomados-derecho-criminologia.webp',
        altBanner: 'Derecho',
        diplomados: [
            {
                id: 'Perfiles',
                img: '/pagesImages/derecho-educacion-continua-humanitas.webp',
                nivel: 'Diplomado en',
                carrera: 'Perfiles Criminales',
                intro: 'Brinda conocimientos en Ciencias Forenses para la impartición de justicia, abordando su evolución y aplicación actual. Con este diplomado, los profesionales podrán diseñar perfiles criminales, analizar la mente del infractor, interpretar evidencias y aplicar técnicas de interrogatorio, victimología y mapeo del delito, con base en la escena del crimen y la conducta del criminal.',
                beneuno: '● Prepara profesionales con conocimientos en Derecho, Medicina, Psicología, Sociología y estadística, esenciales para la investigación criminal.',
                benedos: '● Proporciona herramientas para realizar indagaciones conforme a criterios jurídicos y el marco legal vigente.',
                benetres: '● Capacita en todas las etapas del juicio oral, incluyendo la elaboración de documentos periciales.',
                perfil: 'Dirigido a estudiantes y egresados de la Licenciatura en Derecho, así como a criminalistas y criminólogos. Se requiere copia simple del último comprobante de estudios o cédula profesional.',
                alt: 'Educación Continua en Universidad Humanitas',
                fechas: 'Septiembre y abril.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [ true, false, true, false, true,false,]
            },
            {
                id: 'Criminologia',
                img: '/pagesImages/criminologia.webp',
                nivel: 'Diplomado en',
                carrera: 'Criminología',
                intro: 'Desarrolla conocimientos y capacidad suficiente para enfrentar y comprender el ámbito de la prevención, investigación y persecución de los delitos, con una sólida ética profesional, conciencia y responsabilidad social, para participar activa y adecuadamente en el desarrollo político, económico y social.',
                beneuno: '● Forma especialistas en Criminología de alto nivel profesional, con sólidas bases científicas, tecnológicas y humanísticas.',
                benedos: '● Desarrollo de fundamentos, métodos y técnicas de la criminología, criminalística, Derecho Penal, Psicología, Física, Química y Biología.',
                benetres: '● Estudios de métodos efectivos de análisis matemático, que permitan realizar el estudio, investigación y análisis de hechos delictivos, a fin de identificar las causas que llevan a cometer diversos delitos.',
                perfil: 'Dirigido a perfiles con Licenciatura en Derecho, Ciencias Políticas, así como a profesionistas y estudiantes afines al área (psicólogos, abogados, administradores, educadores).',
                alt: 'Educación Continua en Universidad Humanitas',
                fechas: 'Septiembre y abril.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true, false, true, false, true, false]
            },
            {
                id: 'Derecho',
                img: '/pagesImages/derecho-corporativo-cumplimiento-empresarial-educacion-continua-humanitas.webp',
                nivel: 'Diplomado en',
                carrera: 'Compliance',
                intro: 'Programa de formación profesional orientado a desarrollar conocimientos y herramientas para prevenir, identificar y gestionar riesgos de incumplimiento dentro de las organizaciones. Su enfoque integra aspectos jurídicos, éticos y administrativos relacionados con el cumplimiento normativo, la integridad empresarial y la prevención de conductas que puedan generar responsabilidades legales, económicas o reputacionales.',
                beneuno: '● Desarrolla la capacidad para identificar riesgos legales, regulatorios, operativos y reputacionales, estableciendo controles preventivos dentro de las organizaciones.',
                benedos: '● Proporciona herramientas para diseñar códigos de conducta, políticas de integridad, procesos de debida diligencia, canales de denuncia y mecanismos de control.',
                benetres: '● Favorece una cultura organizacional basada en la ética, la transparencia y el cumplimiento.',
                perfil: 'Dirigido a profesionales y estudiantes de últimos semestres de Derecho, Administración, Contabilidad, finanzas, recursos humanos, auditoría, Economía y áreas afines; así como a empresarios, directivos, gerentes, consultores, auditores internos y responsables de cumplimiento, control interno, riesgos o gobierno corporativo.',
                alt: 'Diplomado en Compliance en Universidad Humanitas',
                fechas: 'Enero y julio.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [ false, true, false, true, false,true,]
            },
        ]
    },
    {
        id: 'negocios',
        nombre: 'Negocios y gestión',
        imagenBanner: '/pagesImages/diplomados-negocios.webp',
        altBanner: 'Negocios',
        diplomados: [
            {
                id: 'Liderazgo',
                img: '/pagesImages/diplomado-liderazgo.webp',
                nivel: 'Diplomado en',
                carrera: 'Liderazgo, dirección y alta gerencia',
                complemento: '',
                intro: 'Programa de alta formación para desarrollar las competencias que demandan las organizaciones actuales en sus niveles de dirección y gestión. Integra conocimientos de liderazgo, administración, estrategia, finanzas y desarrollo organizacional.',
                beneuno: '● Fortalece las competencias de liderazgo y dirección de equipos, desarrollando habilidades para motivar, comunicar, negociar y gestionar equipos.',
                benedos: '● Mejora la capacidad para tomar decisiones estratégicas, integrando herramientas de análisis, planeación y finanzas.',
                benetres: '● Impulsa una visión integral de la alta dirección, proporcionando herramientas para gestionar el cambio.',
                perfil: 'Dirigido a mandos medios, gerentes, directivos, empresarios, emprendedores y profesionistas que buscan fortalecer sus capacidades de liderazgo y gestión organizacional.',
                alt: 'Barco de papel liderando una flota de barcos de papel',
                fechas: 'Abril y septiembre.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true, false, true, false,  true, false]
            },
            {
                id: 'Gestión estrategica',
                img: '/pagesImages/diplomado-recursos-humanos.webp',
                nivel: 'Diplomado en',
                carrera: 'Gestión estratégica de recursos humanos',
                complemento: '',
                intro: 'Plan académico especializado que integra conocimiento y mejores prácticas de gestión del talento humano con herramientas tecnológicas y enfoques estratégicos orientados a las necesidades de las organizaciones actuales.',
                beneuno: '● Fortalece la capacidad para atraer, desarrollar y retener talento, mediante herramientas actuales de reclutamiento, employer branding y capacitación.',
                benedos: '● Impulsa una gestión de recursos humanos basada en datos y tecnología.',
                benetres: '● Desarrolla una visión estratégica de la gestión del talento, permitiendo alinear la cultura organizacional, las compensaciones y el desempeño de las personas.',
                perfil: 'Enfocado a profesionales de recursos humanos, administradores, empresarios, gerentes, coordinadores de personal y consultores que desean fortalecer sus capacidades para gestionar estratégicamente el talento humano.',
                alt: 'Gestión estrategia de recursos humanos',
                fechas: 'Septiembre y abril.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true, false, true, false,  true, false]
            },
        ]
    },
    {
        id: 'salud',
        nombre: 'Salud y Bienestar',
        imagenBanner: '/pagesImages/diplomados-salud-y-bienestar.webp',
        altBanner: 'Criminología y seguridad',
        diplomados: [
            {
                id: 'tanatologia',
                img: '/pagesImages/seminario-tanatologia-educacion-continua-humanitas.webp',
                nivel: 'Diplomado en',
                carrera: 'Tanatología',
                intro: 'Se enfoca en el estudio de la muerte, los procesos y las formas en que los seres humanos enfrentan estas situaciones. El programa abarca aspectos médicos, psicológicos, sociales, éticos y espirituales relacionados con la muerte y el duelo.',
                beneuno: '● Brinda una comprensión profunda del significado de la muerte en diferentes culturas, permitiendo desarrollar una aproximación profesional hacia quienes atraviesan un proceso de pérdida.',
                benedos: '● Capacita para acompañar a los deudos con sensibilidad y competencia, fortaleciendo habilidades humanas y técnicas esenciales en contextos de duelo.',
                benetres: '● Ofrece conocimientos sólidos sobre el duelo y sus manifestaciones, así como herramientas para el abordaje terapéutico y psicoemocional.',
                perfil: 'Dirigido a profesionales de la salud física y mental, trabajadores sociales, cuidadores, así como a toda persona interesada en comprender de forma más consciente y reflexiva los procesos asociados a la muerte y el duelo.',
                alt: 'Diplomado en Tanatología de Educación Continúa en Universidad Humanitas',
                fechas: 'Septiembre y abril.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true, false, true, false, true, false]
            },
            {
                id: 'Bienestar',
                img: '/pagesImages/seminario-tanatologia-educacion-continua-humanitas.webp',
                nivel: 'Diplomado en',
                carrera: 'Bienestar integral, salud mental y Psicología positiva',
                intro: 'Plan académico especializado que integra conocimientos de Psicología, ciencias del comportamiento y el bienestar humano para proporcionar herramientas que permitan mejorar la calidad de vida y promover entornos saludables.',
                beneuno: '● Fortalece el bienestar personal y profesional, mediante herramientas de Psicología positiva, inteligencia emocional y hábitos saludables.',
                benedos: '● Desarrolla recursos para afrontar el estrés y fortalecer la resiliencia.',
                benetres: '● Proporciona herramientas para promover entornos más saludables y positivos, aplicando principios de bienestar y felicidad organizacional.',
                perfil: 'Psicólogos, coaches, profesionales de recursos humanos, docentes, orientadores, consultores y público en general interesado en comprender los factores que favorecen el bienestar humano y la salud mental.',
                alt: 'Diplomado en bienestar integral, salud mental y Psicología positiva de Educación Continúa en Universidad Humanitas',
                fechas: 'Enero y julio.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [ false, true, false, true, false,true,]
            },
            {
                id: 'MFilosofía',
                img: '/pagesImages/diplomado-filosofia-positiva-mindfulness-educacion-continua-humanitas.webp',
                nivel: 'Diplomado en',
                carrera: 'Filosofía positiva',
                intro: 'Plan académico con el fin de profundizar conocimientos y corrientes filosóficas para cuestionar el pensamiento, fortalecer el carácter, afrontar la adversidad, construir relaciones significativas y orientar las decisiones hacia una vida con sentido y propósito, mediante el estudio y aplicación de tradiciones filosóficas occidentales y orientales, así como de perspectivas humanistas y existenciales.',
                beneuno: '● Fortalece el autoconocimiento y el pensamiento crítico, ya que permite cuestionar creencias, analizar decisiones y desarrollar una visión más consciente y reflexiva de la propia vida.',
                benedos: '● Desarrolla recursos para afrontar la adversidad: integra principios de virtud, serenidad, autonomía y responsabilidad.',
                benetres: '● Impulsa una vida con sentido y propósito proporcionando herramientas filosóficas para identificar valores, fortalecer relaciones y construir un proyecto de vida.',
                perfil: 'Enfocado a profesionistas, docentes, directivos, líderes, emprendedores, estudiantes universitarios y público en general interesado en filosofía, humanismo, desarrollo personal, ética, bienestar y construcción de sentido.',
                alt: 'Diplomado filosofía positiva de Educación Continúa en Universidad Humanitas',
                fechas: 'Enero y julio.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [ false, true, false, true, false,true,]
            },
            
        ]
    },
    {
        id: 'tecnologia',
        nombre: 'Tecnología',
        imagenBanner: '/pagesImages/diplomados-tecnologia.webp',
        altBanner: 'Estudiantes de tecnología',
        diplomados: [
            {
                id: 'ia-negocios',
                img: '/pagesImages/diplomado-inteligencia-artificial.webp',
                nivel: 'Diplomado en',
                carrera: 'Inteligencia Artificial aplicada a los negocios',
                complemento: '',
                intro: 'Programa de educación especializado para el desarrollo de conocimientos, habilidades y competencias orientadas al uso estratégico de la inteligencia artificial (IA) en organizaciones de distintos sectores económicos.',
                beneuno: '● Incrementa la productividad y eficiencia profesional con habilidades para utilizar asistentes inteligentes y herramientas de IA.',
                benedos: '● Fortalece la toma de decisiones y la estrategia de negocio.',
                benetres: '● Diseña proyectos de IA aplicados a necesidades reales de las organizaciones y convierte estas tecnologías en herramientas para generar valor.',
                perfil: 'Dirigido a profesionales, directivos, emprendedores y estudiantes de diversas áreas del conocimiento que buscan comprender el potencial de la inteligencia artificial y aprovechar sus aplicaciones para mejorar la eficiencia, la innovación y la competitividad.',
                alt: 'Inteligencia artificial impulsando los negocios',
                fechas: 'Septiembre, enero y julio.',
                duracion: '2 bloques trimestrales (6 meses)',
                inicios: [true, true, false, true, false, true]
            }
        ]
    },
]);

// Categoría Activa
const categoriaActivaId = ref<string>(categorias.value[0]?.id || 'psicologia');

const categoriaActiva = computed(() => {
  return categorias.value.find(cat => cat.id === categoriaActivaId.value) || categorias.value[0];
});

// Filtros para la tabla
const filtroArea = ref<string>(categoriaActivaId.value);
const filtroFecha = ref<string>('todas');

const seleccionarCategoria = (catId: string) => {
  categoriaActivaId.value = catId;
  filtroArea.value = catId;
};

// Sincronizar el <select> con la categoría activa
watch(filtroArea, (nuevaArea) => {
  if (nuevaArea !== 'todas' && nuevaArea !== categoriaActivaId.value) {
    categoriaActivaId.value = nuevaArea;
  }
});

watch(categoriaActivaId, (nuevaCat) => {
  if (nuevaCat && filtroArea.value !== nuevaCat) {
    filtroArea.value = nuevaCat;
  }
});

// Filtrado dinámico para la tabla de próximos inicios
const categoriasFiltradasTabla = computed(() => {
  return categorias.value.map(cat => {
    // 1. Filtrar por Área
    if (filtroArea.value !== 'todas' && cat.id !== filtroArea.value) {
      return null;
    }

    // 2. Filtrar diplomados por Fecha dentro de la categoría
    const diplomadosFiltrados = cat.diplomados.filter(dip => {
      if (filtroFecha.value === 'todas') return true;
      const indexPeriodo = periodos.value.findIndex(p => p.id === filtroFecha.value);
      return indexPeriodo !== -1 ? dip.inicios[indexPeriodo] : true;
    });

    if (diplomadosFiltrados.length === 0) {
      return null;
    }

    return {
      ...cat,
      diplomados: diplomadosFiltrados
    };
  }).filter(Boolean) as Categoria[];
});
</script>

<template>
  <TopBannerLeft
    :img="'background-image: url(/pagesImages/teachers_bg.jpg);'"
    :nivel="'Educaci&oacute;n'"
    :carrera="'Continua'"
    :frase="'Es una filosof&iacute;a de vida, ya que nunca se deja de aprender'"
    :continua="'true'"
  />

  <section class="flex flex-col w-full justify-center items-center py-8">
    <!-- Header inicial del Centro -->
    <div class="flex items-center justify-center w-full">
      <div class="flex flex-col items-center my-4 justify-center w-full lg:px-4 px-8">
        <h1 class="text-hgold font-serif text-3xl mb-4">Centro de Educaci&oacute;n Continua Humanitas</h1>
        <p class="text-left md:text-justify w-full lg:w-[50%] lg:mx-0 mx-12 mb-6">
          Nuestro Centro de Educación Continua es una oportunidad para quienes buscan destacar en su ámbito profesional, además de actualizarse o especializarse.
        </p>
        <router-link to="/contacto">
          <div class="flex items-center justify-center w-[140px] h-[35px] hover:bg-hwine bg-hgold text-white text-lg transition duration-300 ease-in-out">
            <span>Inscr&iacute;bete</span>
          </div>
        </router-link>
      </div>   
    </div>

    <!-- SECCIÓN: OFERTA ACADÉMICA -->
    <div class="flex items-center justify-center w-full">
      <div class="w-full max-w-[1200px] lg:px-0 px-4 my-8">
        <h2 class="text-hgold font-serif text-3xl text-center mb-8">Diplomados</h2>

        <!-- Botones de Categorías -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 w-full mb-12">
          <button
            v-for="cat in categorias"
            :key="cat.id"
            @click="seleccionarCategoria(cat.id)"
            :class="[
              'py-2 text-sm h-16 text-[15px] focus:outline-none transition duration-300 rounded-none flex items-center justify-center text-center',
              categoriaActivaId === cat.id
                ? 'bg-hgold text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            {{ cat.nombre }}
          </button>
        </div>

        <!-- Título de la Categoría Activa -->
        <div v-if="categoriaActiva" class="text-center mb-10">
          <h3 class="text-hgold font-serif text-3xl">{{ categoriaActiva.nombre }}</h3>
        </div>

        <!-- LISTADO DE TODOS LOS DIPLOMADOS INTERCALADOS (LEFT / RIGHT) -->
        <div v-if="categoriaActiva && categoriaActiva.diplomados.length > 0" class="flex flex-col space-y-12 w-full">
          <template v-for="(dip, index) in categoriaActiva.diplomados" :key="dip.id">
            
            <!-- Componente Izquierda (Índices pares: 0, 2, 4...) -->
            <DiplomadoLeft
              v-if="index % 2 === 0"
              :img="dip.img"
              :nivel="dip.nivel"
              :carrera="dip.carrera"
              :complemento="dip.complemento"
              :intro="dip.intro"
              :beneuno="dip.beneuno"
              :benedos="dip.benedos"
              :benetres="dip.benetres"
              :perfil="dip.perfil"
              :alt="dip.alt"
              :fechas="dip.fechas"
              :duracion="dip.duracion"
            />

            <!-- Componente Derecha (Índices impares: 1, 3, 5...) -->
            <DiplomadoRight
              v-else
              :img="dip.img"
              :nivel="dip.nivel"
              :carrera="dip.carrera"
              :complemento="dip.complemento"
              :intro="dip.intro"
              :beneuno="dip.beneuno"
              :benedos="dip.benedos"
              :benetres="dip.benetres"
              :perfil="dip.perfil"
              :alt="dip.alt"
              :fechas="dip.fechas"
              :duracion="dip.duracion"
            />

          </template>
        </div>

        <p v-else-if="categoriaActiva" class="text-center text-gray-500 italic my-12">
          Pr&oacute;ximamente ofertaremos diplomados en esta &aacute;rea.
        </p>
      </div>
    </div>

    <!-- SECCIÓN: CALENDARIO -->
    <div class="flex items-center justify-center w-full my-12">
      <div class="w-full max-w-[1200px] lg:px-0 px-4">
        
        <!-- ENCABEZADO CENTRADO -->
        <div class="text-center mb-8">
          <h2 class="lg:text-[32px] md:text-[28px] text-[24px] text-hgold font-serif font-normal">
            Calendario
          </h2>
          <p class="text-gray-500 text-sm mt-1">
            Consulta la disponibilidad de tu diplomado
          </p>
        </div>

        <!-- CONTROLES Y LEYENDA -->
        <div class="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <!-- Filtros Select -->
          <div class="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <!-- <select
              id="filtro-area"
              v-model="filtroArea"
              class="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:ring-1 focus:ring-hwine cursor-pointer shadow-sm transition duration-200"
            >
              <!-- <option value="todas">Todas las áreas</option> -->
              <!-- <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                {{ cat.nombre }}
              </option>
            </select> --> 

            <select
              id="filtro-fecha"
              v-model="filtroFecha"
              class="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:ring-1 focus:ring-hwine cursor-pointer shadow-sm transition duration-200"
            >
              <option value="todas">Todas las fechas</option>
              <option v-for="per in periodos" :key="per.id" :value="per.id">
                {{ per.mes }} {{ per.periodo }}
              </option>
            </select>
          </div>

          <!-- Leyenda visual -->
          <div class="flex items-center gap-5 text-sm text-gray-600">
            <div class="flex items-center gap-2">
              <span class="w-3.5 h-3.5 rounded-full bg-hwine inline-block"></span>
              <span>Disponible</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="w-4 h-[2px] bg-gray-300 inline-block"></span>
              <span>No disponible</span>
            </div>
          </div>
        </div>

        <!-- TABLA DE INICIOS -->
        <div class="w-full overflow-x-auto">
  <table class="w-full min-w-[520px] text-left border-collapse">
    <thead>
      <tr class="border-b border-gray-200">
        <!-- Encabezado fijo con el nombre dinámico de la categoría activa -->
        <th class="sticky left-0 z-20 bg-white w-1/2 sm:w-2/5 py-3 pr-3 text-hgold font-serif font-semibold text-sm sm:text-base align-bottom max-w-[180px] sm:max-w-none whitespace-normal break-words border-r border-gray-100">
          {{ filtroArea === 'todas' ? 'Diplomados' : categoriaActiva?.nombre }}
        </th>
        <th
          v-for="per in periodos"
          :key="per.id"
          class="text-center py-2 px-2 font-serif text-sm font-normal text-gray-700 leading-tight min-w-[70px]"
        >
          <div class="font-semibold">{{ per.mes }}</div>
          <div class="text-gray-500 text-xs">{{ per.periodo }}</div>
        </th>
      </tr>
    </thead>
    <tbody v-if="categoriasFiltradasTabla.length > 0">
      <template v-for="cat in categoriasFiltradasTabla" :key="cat.id">
        <!-- Filas de Diplomados -->
        <tr
          v-for="(dip, idx) in cat.diplomados"
          :key="dip.id"
          class="group border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
        >
          <!-- Celda de Diplomado -->
          <td class="sticky left-0 z-10 bg-white group-hover:bg-gray-50 transition-colors py-3 pr-3 text-xs sm:text-sm text-hgray max-w-[180px] sm:max-w-none whitespace-normal break-words border-r border-gray-100">
            <span class="mr-1 text-hgray font-medium hidden">{{ idx + 1 }}.</span>
            <span>{{ dip.carrera }}</span>
          </td>

          <!-- Indicadores de estado -->
          <td
            v-for="(activo, pIndex) in dip.inicios"
            :key="pIndex"
            class="text-center py-3 px-2 align-middle"
          >
            <span
              v-if="activo"
              class="inline-block w-3.5 h-3.5 rounded-full bg-hwine align-middle"
              title="Disponible"
            ></span>
            <span
              v-else
              class="inline-block w-4 h-[2px] bg-gray-300 align-middle"
              title="No disponible"
            ></span>
          </td>
        </tr>
      </template>
    </tbody>
    <!-- Estado vacío -->
    <tbody v-else>
      <tr>
        <td :colspan="periodos.length + 1" class="py-8 text-center text-gray-500 italic">
          No se encontraron diplomados con los filtros seleccionados.
        </td>
      </tr>
    </tbody>
  </table>
</div>

      </div>
    </div>

    <!-- SECCIÓN: POLÍTICA IA -->
    <section class="flex justify-center lg:mt-6 mb-16 w-full">
      <div class="lg:flex flex-col lg:px-0 px-8 items-center w-full max-w-4xl lg:max-w-[1200px] justify-start">
        <div class="flex flex-col justify-between w-full">
          <div class="lg:flex justify-center w-full">
            <div class="flex items-center justify-center w-full lg:w-1/2">
              <img src="/pagesImages/herramientas-inteligencia-artificial-humanitas.webp" alt="Herramientas de inteligencia artificial aplicadas en Universidad Humanitas" class="w-full "/>
            </div>
            <div class="lg:w-[50%] text-justify mt-8 lg:mt-0 lg:ml-12">
              <h2>
                <span class="block leading-none text-left text-[28px]">Política y uso de herramientas IA en</span>
                <span class="sr-only whitespace-nowrap">&nbsp;</span>
                <span class="block text-hgold text-left font-serif mb-3 text-[42px]">Universidad Humanitas</span>
              </h2>
              <div class="text-justify">
                <p class="mb-4 lg:hyphens-manual hyphens-auto">En Humanitas entendemos que el mundo está cambiando a gran velocidad. La Inteligencia Artificial ya forma parte del día a día de los estudiantes, y en lugar de rechazarla, enseñamos a aprovecharla con inteligencia, ética y sentido crítico.</p>
                <p class="mb-4 lg:hyphens-manual hyphens-auto">Los alumnos no solo aprenderán a usar herramientas como ChatGPT, editores inteligentes o plataformas personalizadas. Desarrollarán capacidad de pensamiento crítico y analítico con el objetivo de debatir y defender las ideas, combinando tecnología avanzada con el desarrollo humano.</p>
                <p class="mb-4 lg:hyphens-manual hyphens-auto">Nuestro modelo académico integra la IA como una aliada del aprendizaje, pero nunca como un sustituto del pensamiento. Formamos para enfrentar el futuro con criterio, creatividad y preparación real.</p>
                <p class="font-semibold text-center">Estudia en Humanitas, donde la tecnología potencia tu mente, no la reemplaza.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>